import React, { useEffect, useState } from "react";
import { Users2, Globe2 } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { useMouseParallax } from "@/hooks/useMouseParallax";

/**
 * ABOUT HERO — "Constellation Crew"
 * SVG constellation that parallax-follows the cursor.
 * Rotating circular-text disc. Live Sofia clock.
 */
const AboutHero = () => {
  const { t, lang } = useLang();
  const { ref, pos } = useMouseParallax();
  const [time, setTime] = useState("");

  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const f = new Intl.DateTimeFormat(lang === "bg" ? "bg-BG" : "en-GB", {
        hour: "2-digit", minute: "2-digit", second: "2-digit",
        timeZone: "Europe/Sofia",
      }).format(now);
      setTime(f);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [lang]);

  // Fixed star points for constellation
  const stars = [
    { x: 12, y: 22, r: 2.4, k: 1 }, { x: 28, y: 14, r: 1.6, k: 0.6 },
    { x: 46, y: 28, r: 3, k: 1 },   { x: 62, y: 18, r: 1.8, k: 0.7 },
    { x: 78, y: 34, r: 2.4, k: 1 }, { x: 88, y: 54, r: 1.6, k: 0.6 },
    { x: 72, y: 68, r: 2.8, k: 1 }, { x: 54, y: 80, r: 1.8, k: 0.7 },
    { x: 34, y: 72, r: 2.4, k: 1 }, { x: 18, y: 58, r: 1.6, k: 0.6 },
    { x: 8, y: 42, r: 2.2, k: 0.8 },{ x: 40, y: 48, r: 1.6, k: 0.6 },
  ];
  const lines = [
    [0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [5, 6], [6, 7],
    [7, 8], [8, 9], [9, 10], [10, 0], [2, 11], [11, 6], [11, 8],
  ];

  const circleText = (lang === "bg"
    ? "PRIME · COLLECTIVE · БЪЛГАРСКА · АГЕНЦИЯ · "
    : "PRIME · COLLECTIVE · INDEPENDENT · AGENCY · "
  ).repeat(2);

  return (
    <section
      ref={ref}
      data-testid="about-hero"
      className="relative border-b border-brand-border overflow-hidden min-h-[88vh] flex items-center"
    >
      {/* Background constellation */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ transform: `translate3d(${pos.x * -20}px, ${pos.y * -20}px, 0)` }}
      >
        <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
          {lines.map(([a, b], i) => {
            const s1 = stars[a], s2 = stars[b];
            return (
              <line
                key={i}
                x1={s1.x} y1={s1.y} x2={s2.x} y2={s2.y}
                stroke="url(#constellation-grad)"
                strokeWidth="0.08"
                opacity="0.5"
              />
            );
          })}
          {stars.map((s, i) => (
            <circle key={i} cx={s.x} cy={s.y} r={s.r / 10} fill="#C084FC" opacity={s.k}>
              <animate attributeName="opacity" values={`${s.k};${s.k * 0.3};${s.k}`} dur={`${2 + (i % 4)}s`} repeatCount="indefinite" />
            </circle>
          ))}
          <defs>
            <linearGradient id="constellation-grad" x1="0" x2="1">
              <stop offset="0%" stopColor="#A855F7" />
              <stop offset="50%" stopColor="#EC4899" />
              <stop offset="100%" stopColor="#34D399" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      <div className="absolute inset-0 pointer-events-none">
        <div className="orb orb-purple w-[600px] h-[600px] -top-40 -left-32 opacity-25 animate-float-slow" />
        <div className="orb orb-teal w-[420px] h-[420px] bottom-0 right-10 opacity-20 animate-float-slower" />
      </div>

      {/* Mouse spotlight */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(500px circle at ${(pos.x + 0.5) * 100}% ${(pos.y + 0.5) * 100}%, rgba(168,85,247,0.18), transparent 60%)`,
        }}
      />

      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10 py-20 pt-5 md:py-28 md:pt-7 w-full grid lg:grid-cols-12 gap-12 items-center">
        {/* Left column */}
        <div className="lg:col-span-8">
          <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full border border-brand-border bg-brand-surface/60 backdrop-blur-sm mb-8">
            <Globe2 className="w-3.5 h-3.5 text-brand-tealLight" />
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-300">
              {lang === "bg" ? "София · БЪЛГАРИЯ" : "Sofia · BULGARIA"}
            </span>
            <span className="font-mono text-[10px] text-brand-tealLight">{time}</span>
          </div>

          <p className="text-xs font-mono uppercase tracking-[0.35em] text-brand-purpleLight mb-6 ml-1">{t.about.tag}</p>

          <h1 className="font-heading text-[2.8rem] md:text-[4.6rem] lg:text-[5.8rem] font-black uppercase leading-[0.88] tracking-tighter">
            <span className="block">{lang === "bg" ? "Колектив" : "A collective"}</span>
            <span className="block">
              <span className="chrome-text">{lang === "bg" ? "от неспокойни" : "of restless"}</span>
            </span>
            <span className="block holo-text glitch" data-text={lang === "bg" ? "умове." : "minds."}>
              {lang === "bg" ? "умове." : "minds."}
            </span>
          </h1>

          <p className="mt-8 max-w-2xl text-zinc-400 leading-relaxed text-base md:text-lg">{t.about.sub}</p>

          {/* Anchor stats */}
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-[1px] rounded-2xl overflow-hidden border border-brand-border bg-brand-border max-w-2xl">
            {[
              { v: "6+", l: lang === "bg" ? "Години" : "Years" },
              { v: "80+", l: lang === "bg" ? "Клиенти" : "Clients" },
              { v: "12", l: lang === "bg" ? "Специалисти" : "Specialists" },
              { v: "24ч", l: lang === "bg" ? "Отговор" : "Response" },
            ].map((s) => (
              <div key={s.l} className="bg-brand-bg/80 backdrop-blur-md px-5 py-4 text-left">
                <p className="font-heading text-2xl md:text-3xl font-black tracking-tighter text-white">{s.v}</p>
                <p className="mt-1 text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-500">{s.l}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Right column — rotating disc */}
        <div className="lg:col-span-4 flex items-center justify-center">
          <div className="relative w-[280px] h-[280px] md:w-[340px] md:h-[340px]">
            {/* Rotating text ring */}
            <svg className="absolute inset-0 w-full h-full animate-[holo-rotate_28s_linear_infinite]" viewBox="0 0 200 200">
              <defs>
                <path id="about-circle" d="M 100, 100 m -85, 0 a 85,85 0 1,1 170,0 a 85,85 0 1,1 -170,0" />
              </defs>
              <text fill="#C084FC" fontSize="10" fontFamily="monospace" letterSpacing="4">
                <textPath href="#about-circle">{circleText}</textPath>
              </text>
            </svg>
            {/* Inner core */}
            <div className="absolute inset-10 rounded-full border border-brand-purple/40 grid place-items-center bg-brand-bg/60 backdrop-blur-md">
              <div className="text-center">
                <Users2 className="w-8 h-8 mx-auto text-brand-purpleLight mb-3" strokeWidth={1.5} />
                <p className="font-heading text-lg font-bold uppercase tracking-tight text-white">
                  {lang === "bg" ? "EST. 2019" : "EST. 2019"}
                </p>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mt-1">
                  PRIME · CLTV
                </p>
              </div>
            </div>
            {/* Outer dots */}
            {[0, 60, 120, 180, 240, 300].map((deg) => (
              <span
                key={deg}
                className="absolute top-1/2 left-1/2 w-2 h-2 rounded-full bg-brand-pink shadow-[0_0_12px_rgba(236,72,153,0.8)]"
                style={{
                  transform: `rotate(${deg}deg) translateY(-170px) translate(-50%, -50%)`,
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutHero;
